import React, { useEffect, useState } from 'react';
import { NOT_AUTHORIZED } from '../services/api';

// Signed in with Google, but not an active FinOps user. The backend gives the
// same answer for unknown, disabled and non-Eyvo accounts, and so does this
// page, so it cannot be used to discover who has access. domain_blocked and
// account_deactivated are the codes older deployments sent.
const ACCESS_DENIED_CODES = new Set(['not_authorized', 'domain_blocked', 'account_deactivated']);

export default function Login() {
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('auth_error');
    if (ACCESS_DENIED_CODES.has(authError)) {
      setDenied(true);
    } else if (authError === 'cancelled') {
      setError('');
    } else if (authError) {
      setError('Sign-in failed. Please try again.');
    }
  }, []);

  const [loading, setLoading] = useState(false);

  // A full-page navigation to the API, not a fetch: the API binds the sign-in
  // to this browser with a cookie on its own host before sending it to Google.
  const handleGoogleLogin = () => {
    setLoading(true);
    setError('');
    setDenied(false);
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-indigo-900 rounded-lg flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>E</span>
            </div>
            <h1
              className="text-2xl font-bold text-slate-900 mb-1"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="login-title"
            >
              Eyvo Finance Ops
            </h1>
            <p className="text-sm text-slate-500">Internal financial control system</p>
          </div>

          <div className="space-y-4">
            {denied && (
              <div
                role="alert"
                className="bg-amber-50 border border-amber-200 text-amber-900 text-sm px-4 py-3 rounded-md"
                data-testid="access-denied"
              >
                <p className="font-semibold">{NOT_AUTHORIZED}</p>
                <p className="mt-1 text-amber-800">
                  Ask a FinOps administrator to add your exact Google email address, or sign in
                  with a different account.
                </p>
              </div>
            )}
            {error && (
              <div
                className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              data-testid="google-signin-button"
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-all duration-150 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 002.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.01c-.72.48-1.63.75-2.7.75-2.08 0-3.84-1.4-4.47-3.29H1.84v2.07A8 8 0 008.98 17z"/>
                <path fill="#FBBC05" d="M4.51 10.51A4.8 4.8 0 014.26 9c0-.52.09-1.03.25-1.51V5.42H1.84A8 8 0 000.98 9c0 1.28.31 2.5.86 3.58l2.67-2.07z"/>
                <path fill="#EA4335" d="M8.98 3.58c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 008.98 1a8 8 0 00-7.14 4.42l2.67 2.07c.63-1.9 2.39-3.3 4.47-3.3v-.61z"/>
              </svg>
              {loading ? 'Connecting...' : 'Sign in with Google'}
            </button>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            Sign in with your Eyvo Google Workspace account
          </p>

          <div className="mt-6 pt-4 border-t border-slate-100 text-center" data-testid="version-info">
            <p className="text-xs text-slate-400 font-mono">v2.1.0 — Built 2026-04-18 15:23 PST</p>
          </div>
        </div>
      </div>
    </div>
  );
}
